import React, { createContext, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { content, LANG_NAMES } from "../i18n";

const LanguageContext = createContext(null);

const SUPPORTED = Object.keys(LANG_NAMES); // ["de", "en"]
const DEFAULT_LANG = "de";

const getSavedLang = () => {
  const saved = localStorage.getItem("pi_lang");
  return SUPPORTED.includes(saved) ? saved : DEFAULT_LANG;
};

// LanguageProvider MUST be rendered inside <BrowserRouter> (it reads the URL).
export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const segment = location.pathname.split("/")[1];
  const urlLang = SUPPORTED.includes(segment) ? segment : null;
  const lang = urlLang || getSavedLang();

  // Keep the URL language-prefixed and persist the choice.
  useEffect(() => {
    if (urlLang) {
      localStorage.setItem("pi_lang", urlLang);
      document.documentElement.lang = urlLang;
    } else {
      const target = location.pathname === "/" ? "" : location.pathname;
      navigate(`/${getSavedLang()}${target}${location.search}`, { replace: true });
    }
  }, [location.pathname, location.search, urlLang, navigate]);

  // Build a language-prefixed path from an unprefixed path like "/about".
  const lp = (path = "/") => {
    let clean;
    if (path === "/") {
      clean = "";
    } else if (path.startsWith("/")) {
      clean = path;
    } else {
      clean = `/${path}`;
    }
    return `/${lang}${clean}`;
  };

  // Switch language while staying on the current page.
  const setLang = (code) => {
    if (!SUPPORTED.includes(code)) return;
    localStorage.setItem("pi_lang", code);
    const parts = location.pathname.split("/");
    if (SUPPORTED.includes(parts[1])) {
      parts[1] = code;
      navigate(parts.join("/") + location.search);
    } else {
      navigate(`/${code}`);
    }
  };

  const value = {
    lang,
    setLang,
    lp,
    t: content[lang],
    langName: LANG_NAMES[lang],
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};
